// @ts-strict-ignore
import * as React from 'react';
import { Footer } from 'antd/lib/layout/layout';
import Space from 'antd/lib/space';
import Button from 'antd/lib/button';
import Modal from 'antd/lib/modal';
import Checkbox from 'antd/lib/checkbox';
import Flex from 'antd/lib/flex';
import Text from 'antd/lib/typography/Text';
import Divider from 'antd/lib/divider';
import Form from 'antd/lib/form';
import Tooltip from 'antd/lib/tooltip';
import Alert from 'antd/lib/alert';
import {
    BugOutlined,
    CaretRightOutlined,
    DownOutlined,
    FireOutlined,
    LoadingOutlined,
    PauseOutlined,
    PlayCircleOutlined,
    RiseOutlined,
    RocketOutlined,
    SearchOutlined,
    SlackOutlined,
} from '@ant-design/icons';
import { ButtonProps } from 'antd/lib/button';
import Collapse from 'antd/lib/collapse';
import Dropdown from 'antd/lib/dropdown';
import { MenuProps } from 'antd/lib/menu';
import { flatten } from 'lodash';

import { StepRun } from '@/types/stepRun';
import {
    AmazonAppStoreData,
    AppleAppStoreData,
    AppPlatform,
    ChecklistItemStatusType,
    GooglePlayStoreData,
    HuaweiAppStoreData,
    ReleaseStepApprovalsData,
    ReleaseStepRegressionTestingData,
    RegressionTestingStrategy,
    ReleaseStepRolloutData,
    ReleaseStepStatus,
    ReleaseStepSubmissionData,
    RunwayResponseType,
    SamsungAppStoreData,
    TransactionGroupType,
    TransactionType,
    RegressionStatus,
} from '@/types';
import styles from '../../styles/stepStyles';
import { antGrey, runwayColors } from '../../styles/globalStyles';
import { StepStatusAlert } from '../../StepStatusHeaderComponent';
import WorkflowsPlatformIcon from '../../icons/WorkflowsPlatformIcon';
import { getStepRunSummaryPanelByArchetype } from '../archetypeSpecificStepRunSummaryPanels';
import { globalMessage } from '@/utils/globalMessage';
import { TransactionStatus } from '@/types/batchTransaction';
import { WorkflowRunUpdatable } from '@/actions/workflowRun';
import { useCallUpdatable } from '@/utils/hooks/helpers';
import { useSelector } from 'react-redux';
import {
    selectIsAppsDemoModeEnabled,
    selectReleaseVersion,
    selectShouldWarnAboutNewerCommits,
    selectStepRunsFromSummary,
    selectWorkflowId,
    selectWorkflowRunId,
} from '@/selectors/selectors';
import { RootState } from '@/reducers/reducers';
import {
    appleAppCanBeDeveloperRejected,
    getIsAppleAppStore,
    isAppReleased,
    isAppSubmitted,
    isPhasedReleaseComplete,
    isPhasedReleasePaused,
    isPhasedReleaseSuperseded,
} from '@/helpers';
import Select from 'antd/lib/select';
import { RegressionTestingStepSettings } from '@/types/stepConfig';
import Spin from 'antd/lib/spin';
import { useStepRunsSummary } from '@/utils/hooks/useStepRunsSummary';
import { useInfiniteScroll } from '@/utils/hooks/useInfiniteScroll';
import { StepRunStatusData } from '@/types/stepRunStatus';

// Similar to ReleaseStepStatus, but merges Pending and PendingChecklistOnly into a single statuses
const stepRunSummaryStatuses = ['pending', 'ready', 'blocked', 'inactive', 'skipped'] as const;
type StepRunSummaryStatus = (typeof stepRunSummaryStatuses)[number];

interface StepRunsStatusBucket {
    totalCount: number;
    stepRunIds: string[];
}

function buildStepRunsByStatus(
    stepRunStatusData: StepRunStatusData[],
): Record<StepRunSummaryStatus, StepRunsStatusBucket> {
    const byStatus: Record<StepRunSummaryStatus, StepRunsStatusBucket> = {
        pending: { totalCount: 0, stepRunIds: [] },
        ready: { totalCount: 0, stepRunIds: [] },
        blocked: { totalCount: 0, stepRunIds: [] },
        inactive: { totalCount: 0, stepRunIds: [] },
        skipped: { totalCount: 0, stepRunIds: [] },
    };

    for (const sd of stepRunStatusData) {
        let status: StepRunSummaryStatus | null = null;
        switch (sd.status) {
            case ReleaseStepStatus.Pending:
            case ReleaseStepStatus.PendingChecklistOnly:
                status = 'pending';
                break;
            case ReleaseStepStatus.Ready:
                status = 'ready';
                break;
            case ReleaseStepStatus.Blocked:
                status = 'blocked';
                break;
            case ReleaseStepStatus.Inactive:
                status = 'inactive';
                break;
            case ReleaseStepStatus.Skipped:
                status = 'skipped';
                break;
            default:
                status = null;
                break;
        }
        if (!status) continue;

        const statusBucket = byStatus[status];
        statusBucket.totalCount += 1;

        if (sd.stepRunId === null) continue;

        statusBucket.stepRunIds.push(sd.stepRunId);
    }
    return byStatus;
}

export interface BatchTransactionBarProps {
    orgId: string;
    appId: string;
    releaseId: string;
    stepRunStatusData: StepRunStatusData[];
    transactionTypes: TransactionType[];
}

export const actionForTransactionType = (transactionType: TransactionType): string => {
    switch (transactionType) {
        case TransactionType.SubmitVersion:
            return 'Confirm and submit for review...';
        case TransactionType.ReleaseVersion:
            return 'Confirm and release version...';
        case TransactionType.RejectAppStoreRelease:
            return 'Confirm and pull from review...';
        case TransactionType.RunWorkflow:
            return 'Confirm and run workflow...';
        case TransactionType.StartHotfix:
            return 'Start hotfix...';
        case TransactionType.HaltRollout:
            return 'Confirm and pause rollout...';
        case TransactionType.AccelerateRollout:
            return 'Confirm and release to 100%...';
        case TransactionType.ResumeRollout:
            return 'Confirm and resume rollout...';
        case TransactionType.UpdateRegressionStatus:
            return 'Confirm and update regression testing status...';
        case TransactionType.PingPendingRegressionItems:
            return 'Confirm and ping pending regression items...';
        case TransactionType.PingPendingApprovalItems:
            return 'Confirm and ping pending approval items...';
    }
};

export const modalTitleForTransactionType = (transactionType: TransactionType): string => {
    switch (transactionType) {
        case TransactionType.SubmitVersion:
            return 'Confirm and submit update for review';
        case TransactionType.ReleaseVersion:
            return 'Confirm and release version';
        case TransactionType.RejectAppStoreRelease:
            return 'Confirm and pull from review';
        case TransactionType.RunWorkflow:
            return 'Confirm and run workflow';
        case TransactionType.StartHotfix:
            return 'Start hotfix';
        case TransactionType.HaltRollout:
            return 'Pause rollout';
        case TransactionType.AccelerateRollout:
            return 'Release to 100%';
        case TransactionType.ResumeRollout:
            return 'Resume rollout';
        case TransactionType.UpdateRegressionStatus:
            return 'Confirm and update regression testing status';
        case TransactionType.PingPendingRegressionItems:
            return 'Ping all pending regression items';
        case TransactionType.PingPendingApprovalItems:
            return 'Ping all pending approval items';
    }
};

export const modalDescriptionForTransactionType = (transactionType: TransactionType): string => {
    switch (transactionType) {
        case TransactionType.SubmitVersion:
            return 'Selected step configs below will be submitted to their respective app stores for review.';
        case TransactionType.ReleaseVersion:
            return 'Selected step configs below will be released to their respective app stores.';
        case TransactionType.RejectAppStoreRelease:
            return 'Selected step configs below will be pulled from review.';
        case TransactionType.RunWorkflow:
            return 'Selected step configs below will have their CI/CD workflows triggered.';
        case TransactionType.StartHotfix:
            return 'Selected step configs below will have a hotfix started.';
        case TransactionType.HaltRollout:
            return 'Selected step configs below will have their rollouts paused.';
        case TransactionType.AccelerateRollout:
            return 'Selected step configs below will have their rollouts released to 100%.';
        case TransactionType.ResumeRollout:
            return 'Selected step configs below will have their rollouts resumed.';
        case TransactionType.UpdateRegressionStatus:
            return 'Selected step configs below will have their overall regression testing status changed to the selected status.';
        case TransactionType.PingPendingRegressionItems:
            return 'Selected step configs below will have any pending regression testing items pinged.';
        case TransactionType.PingPendingApprovalItems:
            return 'Selected step configs below will have any pending approval items pinged.';
    }
};

export const confirmButtonTextForTransactionType = (
    transactionType: TransactionType,
    selectedCount: number,
): string => {
    switch (transactionType) {
        case TransactionType.SubmitVersion:
            return `Confirm and submit update for ${selectedCount} step config${selectedCount !== 1 ? 's' : ''}`;
        case TransactionType.ReleaseVersion:
            return `Confirm and release ${selectedCount} step config${selectedCount !== 1 ? 's' : ''}`;
        case TransactionType.RejectAppStoreRelease:
            return `Pull ${selectedCount} step config${selectedCount !== 1 ? 's' : ''} from review`;
        case TransactionType.RunWorkflow:
            return `Run workflow for ${selectedCount} step config${selectedCount !== 1 ? 's' : ''}`;
        case TransactionType.StartHotfix:
            return `Start hotfix for ${selectedCount} step config${selectedCount !== 1 ? 's' : ''}`;
        case TransactionType.HaltRollout:
            return `Pause rollout for ${selectedCount} step config${selectedCount !== 1 ? 's' : ''}`;
        case TransactionType.AccelerateRollout:
            return `Release to 100% for ${selectedCount} step config${selectedCount !== 1 ? 's' : ''}`;
        case TransactionType.ResumeRollout:
            return `Resume rollout for ${selectedCount} step config${selectedCount !== 1 ? 's' : ''}`;
        case TransactionType.UpdateRegressionStatus:
            return `Update regression testing status for ${selectedCount} step config${selectedCount !== 1 ? 's' : ''}`;
        case TransactionType.PingPendingRegressionItems:
            return `Ping all pending regression items for ${selectedCount} step config${selectedCount !== 1 ? 's' : ''}`;
        case TransactionType.PingPendingApprovalItems:
            return `Ping all pending approval items for ${selectedCount} step config${selectedCount !== 1 ? 's' : ''}`;
    }
};

export const iconColorForTransactionType = (transactionType: TransactionType): string | undefined => {
    switch (transactionType) {
        case TransactionType.HaltRollout:
            return runwayColors.statusRed;
        case TransactionType.ResumeRollout:
            return runwayColors.statusGreen;
        case TransactionType.AccelerateRollout:
            return runwayColors.statusGreen;
        case TransactionType.StartHotfix:
            return runwayColors.statusYellow;
        default:
            return undefined;
    }
};

export const iconForTransactionType = (
    transactionType: TransactionType,
    options?: { useDefaultColor?: boolean },
): React.ReactNode => {
    const useDefaultColor = options?.useDefaultColor ?? false;
    const color = useDefaultColor ? undefined : iconColorForTransactionType(transactionType);
    const style = color ? { color } : undefined;

    switch (transactionType) {
        case TransactionType.SubmitVersion:
        case TransactionType.RejectAppStoreRelease:
            return <SearchOutlined style={style} />;
        case TransactionType.ReleaseVersion:
            return <RocketOutlined style={style} />;
        case TransactionType.RunWorkflow:
            return <PlayCircleOutlined style={style} />;
        case TransactionType.StartHotfix:
            return <FireOutlined style={style} />;
        case TransactionType.HaltRollout:
            return <PauseOutlined style={style} />;
        case TransactionType.ResumeRollout:
            return <PlayCircleOutlined style={style} />;
        case TransactionType.AccelerateRollout:
            return <RiseOutlined style={style} />;
        case TransactionType.UpdateRegressionStatus:
            return <BugOutlined style={style} />;
        case TransactionType.PingPendingRegressionItems:
            return <SlackOutlined style={style} />;
        case TransactionType.PingPendingApprovalItems:
            return <SlackOutlined style={style} />;
    }
};

export const buttonPropsForTransactionType = (transactionType: TransactionType): Partial<ButtonProps> => {
    switch (transactionType) {
        case TransactionType.StartHotfix:
        case TransactionType.HaltRollout:
        case TransactionType.ResumeRollout:
        case TransactionType.AccelerateRollout:
            return { type: 'default' };
        default:
            return { type: 'primary' };
    }
};

// Transaction Group Helpers

export const getTransactionGroupForType = (transactionType: TransactionType): TransactionGroupType | null => {
    switch (transactionType) {
        case TransactionType.HaltRollout:
        case TransactionType.ResumeRollout:
        case TransactionType.AccelerateRollout:
            return TransactionGroupType.UpdateRollout;
        default:
            return null;
    }
};

export const iconColorForTransactionGroupType = (groupType: TransactionGroupType): string | undefined => {
    switch (groupType) {
        case TransactionGroupType.UpdateRollout:
            return runwayColors.primary;
        default:
            return undefined;
    }
};

export const iconForTransactionGroupType = (groupType: TransactionGroupType): React.ReactNode => {
    const color = iconColorForTransactionGroupType(groupType);
    const style = color ? { color } : undefined;

    switch (groupType) {
        case TransactionGroupType.UpdateRollout:
            return <RiseOutlined style={style} />;
    }
};

export const buttonPropsForTransactionGroupType = (groupType: TransactionGroupType): Partial<ButtonProps> => {
    switch (groupType) {
        case TransactionGroupType.UpdateRollout:
            return { type: 'default' };
    }
};

export const actionForTransactionGroupType = (groupType: TransactionGroupType): string => {
    switch (groupType) {
        case TransactionGroupType.UpdateRollout:
            return 'Update rollout';
    }
};

export const isStepRunSelectableForTransactionOrError = (
    transactionType: TransactionType,
    stepRun: StepRun,
): string | null => {
    const currentlyRunningStepTransaction = stepRun.stepTransactions.find(
        (st) =>
            st.transactionType === transactionType &&
            (st.status === TransactionStatus.Requested || st.status === TransactionStatus.PendingRetry),
    );

    if (currentlyRunningStepTransaction) {
        return `This step is currently running this transaction already. Please wait for it to complete before running another one.`;
    }

    if (stepRun.status === ReleaseStepStatus.Skipped) {
        return 'This step is skipped';
    }

    switch (transactionType) {
        case TransactionType.SubmitVersion: {
            if (stepRun.status === ReleaseStepStatus.Ready) {
                return 'This step is ready and cannot be submitted for review';
            }
            if (stepRun.status === ReleaseStepStatus.Blocked) {
                return 'This step is blocked and cannot be submitted for review';
            }
            return null;
        }
        case TransactionType.ReleaseVersion: {
            if (stepRun.status === ReleaseStepStatus.Ready) {
                return 'This step is ready and cannot be released';
            }
            if (stepRun.status === ReleaseStepStatus.Blocked) {
                return 'This step is blocked and cannot be released';
            }
            return null;
        }
        case TransactionType.RejectAppStoreRelease: {
            const stepData: ReleaseStepSubmissionData = {
                ...stepRun.data.summaryData,
                ...stepRun.data.complementaryData,
            };

            const isAppleAppStore = getIsAppleAppStore(stepData.appStoreType);
            if (!isAppleAppStore) {
                return 'The destination for this step is not an Apple app store, so pulling from review is unsupported';
            }

            const appStoreData = stepData.appStoreData as AppleAppStoreData;
            const googleStoreData = stepData.appStoreData as GooglePlayStoreData;
            const amazonData = stepData.appStoreData as AmazonAppStoreData;
            const huaweiData = stepData.appStoreData as HuaweiAppStoreData;
            const samsungData = stepData.appStoreData as SamsungAppStoreData;

            const isSubmitted = isAppSubmitted(
                stepData.appStoreType,
                appStoreData?.versionReviewSubmissionItem?.state,
                appStoreData?.relevantReviewSubmission?.state,
                googleStoreData?.state,
                amazonData?.state,
                huaweiData?.state,
                samsungData?.state,
            );
            const isReleased = isAppReleased(
                stepData.appStoreType,
                appStoreData?.versionReviewSubmissionItem?.state,
                googleStoreData?.state,
                amazonData?.state,
                huaweiData?.state,
                samsungData?.state,
            );

            const appCanBeDeveloperRejected =
                appStoreData ? appleAppCanBeDeveloperRejected(appStoreData?.versionReviewSubmissionItem?.state) : false;

            if (!(appCanBeDeveloperRejected && isSubmitted && !isReleased)) {
                if (isSubmitted && isReleased) {
                    return 'This step has already been fully released to its destination store, and can no longer be pulled from review';
                }
                if (!isSubmitted) {
                    return 'This step has not been submitted for review yet';
                }

                return 'This step is not in a valid state to be pulled from review';
            }

            return null;
        }
        case TransactionType.RunWorkflow: {
            return null;
        }
        case TransactionType.StartHotfix: {
            return null;
        }
        case TransactionType.HaltRollout: {
            const rolloutData: ReleaseStepRolloutData = {
                ...stepRun.data.summaryData,
                ...stepRun.data.complementaryData,
            };
            const phasedRelease = rolloutData.phasedRelease;

            if (!phasedRelease) {
                return 'No phased release is active for this step';
            }

            const phasedReleasePaused = isPhasedReleasePaused(phasedRelease.state);
            if (phasedReleasePaused) {
                return 'The rollout is already paused';
            }

            const rolloutComplete = isPhasedReleaseComplete(phasedRelease);
            if (rolloutComplete) {
                return 'The rollout is already complete';
            }

            const isSuperseded = isPhasedReleaseSuperseded(phasedRelease);
            if (isSuperseded) {
                return 'This version was superseded and cannot be paused';
            }

            return null;
        }
        case TransactionType.ResumeRollout: {
            const rolloutData: ReleaseStepRolloutData = {
                ...stepRun.data.summaryData,
                ...stepRun.data.complementaryData,
            };
            const phasedRelease = rolloutData.phasedRelease;

            if (!phasedRelease) {
                return 'No phased release is active for this step';
            }

            const phasedReleasePaused = isPhasedReleasePaused(phasedRelease.state);
            if (!phasedReleasePaused) {
                return 'The rollout is not paused';
            }

            const rolloutComplete = isPhasedReleaseComplete(phasedRelease);
            if (rolloutComplete) {
                return 'The rollout is already complete';
            }

            return null;
        }
        case TransactionType.AccelerateRollout: {
            const rolloutData: ReleaseStepRolloutData = {
                ...stepRun.data.summaryData,
                ...stepRun.data.complementaryData,
            };
            const phasedRelease = rolloutData.phasedRelease;

            if (!phasedRelease) {
                return 'No phased release is active for this step';
            }

            const rolloutComplete = isPhasedReleaseComplete(phasedRelease);
            if (rolloutComplete) {
                return 'The rollout is already complete';
            }

            return null;
        }
        case TransactionType.UpdateRegressionStatus: {
            const regressionTestingData: ReleaseStepRegressionTestingData = {
                ...stepRun.data.summaryData,
                ...stepRun.data.complementaryData,
            };

            const strategySettings = stepRun.stepConfigSnapshot?.settings as RegressionTestingStepSettings;
            const regressionStrategy = strategySettings?.regressionTestingStrategy;

            if (regressionStrategy === RegressionTestingStrategy.DisableOverallStatus) {
                return 'This step is currently configured to prevent manual updates to the overall regression testing status';
            }

            const availableBuilds = regressionTestingData?.availableBuilds;
            if (!availableBuilds || availableBuilds.length === 0) {
                return 'This step has no available builds for which to update the regression testing status';
            }

            return null;
        }
        case TransactionType.PingPendingRegressionItems: {
            const stepData: ReleaseStepRegressionTestingData = {
                ...stepRun.data.summaryData,
                ...stepRun.data.complementaryData,
            };

            const testItems = stepData.testItems;
            if (!testItems || testItems.length === 0) {
                return 'This step does not have any regression testing items configured';
            }

            const pendingTestItems = testItems?.filter(
                (item) => item.status?.status !== ChecklistItemStatusType.Passed,
            );
            if (pendingTestItems?.length === 0) {
                return 'This step does not have any pending regression testing items to ping';
            }

            return null;
        }
        case TransactionType.PingPendingApprovalItems: {
            const stepData: ReleaseStepApprovalsData = {
                ...stepRun.data.summaryData,
                ...stepRun.data.complementaryData,
            };

            const approvalItems = stepData.approvalItems;
            if (!approvalItems || approvalItems.length === 0) {
                return 'This step does not have any approval items configured';
            }

            const pendingApprovalItems = approvalItems?.filter(
                (item) => item.status?.status !== ChecklistItemStatusType.Approved,
            );
            if (pendingApprovalItems?.length === 0) {
                return 'This step does not have any pending approval items to ping';
            }

            return null;
        }
        default:
            return 'Unknown transaction type';
    }
};

export interface SubmitVersionFormValues {
    selectedStepRunIds: string[];
    releasePreviousSubmissionToAllUsersFirst: boolean;
}

export interface ReleaseVersionFormValues {
    selectedStepRunIds: string[];
}

export type BatchTransactionFormValues = SubmitVersionFormValues | ReleaseVersionFormValues;

export const extraOptionsForTransactionType = (
    transactionType: TransactionType,
    stepRuns: StepRun[],
): React.ReactNode => {
    switch (transactionType) {
        case TransactionType.SubmitVersion:
            return (
                <div style={{ marginBottom: 16 }}>
                    <Form.Item shouldUpdate noStyle>
                        {({ getFieldValue }) => {
                            const selectedStepRunIds = getFieldValue('selectedStepRunIds');
                            const selectedStepRuns = stepRuns.filter((sr) => selectedStepRunIds.includes(sr.id));
                            const someSelectedStepCannotBeIncremented = selectedStepRuns.some((sr) =>
                                sr.destinations.some((d) => d.platform !== AppPlatform.Android),
                            );

                            return (
                                <Form.Item
                                    name="releasePreviousSubmissionToAllUsersFirst"
                                    valuePropName="checked"
                                    initialValue={false}
                                >
                                    <Checkbox disabled={someSelectedStepCannotBeIncremented}>
                                        Increment prior release to 100% before next release.
                                    </Checkbox>
                                </Form.Item>
                            );
                        }}
                    </Form.Item>
                </div>
            );
        case TransactionType.ReleaseVersion:
            return (
                <div style={{ marginBottom: 16 }}>
                    <Form.Item shouldUpdate noStyle>
                        {({ getFieldValue }) => {
                            const selectedStepRunIds = getFieldValue('selectedStepRunIds');
                            const selectedStepRuns = stepRuns.filter((sr) => selectedStepRunIds.includes(sr.id));
                            const someSelectedStepCannotBeIncremented = selectedStepRuns.some((sr) =>
                                sr.destinations.some((d) => d.platform !== AppPlatform.Ios),
                            );

                            return (
                                <Form.Item
                                    name="releasePreviousSubmissionToAllUsersFirst"
                                    valuePropName="checked"
                                    initialValue={false}
                                >
                                    <Checkbox disabled={someSelectedStepCannotBeIncremented}>
                                        Increment prior release to 100% before next release.
                                    </Checkbox>
                                </Form.Item>
                            );
                        }}
                    </Form.Item>
                </div>
            );
        case TransactionType.RejectAppStoreRelease:
            return null;
        case TransactionType.RunWorkflow:
            return null;
        case TransactionType.StartHotfix:
            return null;
        case TransactionType.HaltRollout:
            return null;
        case TransactionType.AccelerateRollout:
            return null;
        case TransactionType.ResumeRollout:
            return null;
        case TransactionType.UpdateRegressionStatus:
            return (
                <div style={{ marginBottom: 16 }}>
                    <Form.Item name="status" label="Regression testing status" rules={[{ required: true }]}>
                        <Select placeholder="Select a regression testing status">
                            <Select.Option key={RegressionStatus.InProgress} value={RegressionStatus.InProgress}>
                                In progress
                            </Select.Option>
                            <Select.Option key={RegressionStatus.Passed} value={RegressionStatus.Passed}>
                                Passed
                            </Select.Option>
                            <Select.Option key={RegressionStatus.Failed} value={RegressionStatus.Failed}>
                                Failed
                            </Select.Option>
                        </Select>
                    </Form.Item>
                </div>
            );
        case TransactionType.PingPendingRegressionItems:
            return null;
        case TransactionType.PingPendingApprovalItems:
            return null;
    }
};

interface ConfirmationStepRunPanelProps {
    stepRun: StepRun;
    orgId: string;
    appId: string;
    releaseVersion: string;
    warnAboutNewerCommits: boolean;
    isSelected: boolean;
    isDisabled: boolean;
    onSelect: () => void;
    errorMessage: string | null;
}

const ConfirmationStepRunPanel: React.FC<ConfirmationStepRunPanelProps> = ({
    stepRun,
    orgId,
    appId,
    releaseVersion,
    warnAboutNewerCommits,
    isSelected,
    isDisabled,
    onSelect,
    errorMessage,
}) => {
    const stepName = stepRun.displayName || stepRun.stepId;
    const archetypeSpecificPanel = getStepRunSummaryPanelByArchetype({
        stepRun,
        orgId,
        appId,
        releaseVersion,
        shouldDisplayCompactVersion: true,
    });

    const hasAppleDestination = stepRun.destinations.some((d) => getIsAppleAppStore(d.appStoreType));
    const showNewerCommitsWarning = warnAboutNewerCommits && hasAppleDestination;

    const panelContent = (
        <div
            style={{
                borderRadius: 8,
                border: `1px solid ${isSelected ? runwayColors.info : antGrey.grey5}`,
                backgroundColor: runwayColors.white,
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.5 : 1,
            }}
            onClick={(e) => {
                e.stopPropagation();
                if (!isDisabled) {
                    onSelect();
                }
            }}
        >
            <Space style={{ marginBottom: 12 }} align="center">
                {stepRun.destinations.map((destination, index) => (
                    <WorkflowsPlatformIcon key={index} platform={destination.platform} width={24} height={24} />
                ))}
                <Text
                    strong
                    style={{
                        fontSize: 16,
                        color: antGrey.grey8,
                    }}
                >
                    {stepName}
                </Text>
            </Space>

            <StepStatusAlert
                status={stepRun.status}
                statusReasonString={stepRun.statusReasonString}
                style={{ marginBottom: 16 }}
            />

            {showNewerCommitsWarning && (
                <Alert
                    closable={false}
                    showIcon
                    message="There are commits on your release branch that are newer than your currently selected release candidate build."
                    type="warning"
                    style={{ marginBottom: 16 }}
                />
            )}

            {archetypeSpecificPanel && <div>{archetypeSpecificPanel}</div>}
        </div>
    );

    if (errorMessage) {
        return <Tooltip title={errorMessage}>{panelContent}</Tooltip>;
    }

    return panelContent;
};

const SmallSpinnerIndicator = <LoadingOutlined spin style={{ fontSize: 14, color: runwayColors.info }} />;

interface ConfirmationStepStatusSectionProps {
    status: StepRunSummaryStatus;
    title: string;
    color: string;
    orgId: string;
    appId: string;
    warnAboutNewerCommits: boolean;
    workflowId: string;
    workflowRunId: string;
    visible: boolean;
    releaseVersion: string;
    transactionType: TransactionType;
    totalCount: number;
    allStepRunIds: string[];
    scrollRootRef: React.RefObject<HTMLDivElement | null>;
    isDisabled?: boolean;
    selectedStepRunIds: Set<string>;
    onRegisterFetchAll: (status: StepRunSummaryStatus, fetchAll: () => Promise<StepRun[]>) => void;
    onUnregisterFetchAll: (status: StepRunSummaryStatus) => void;
    onStepSelectionChange: (stepRunId: string, checked: boolean) => void;
    onSectionSelectionChange: (checked: boolean, stepRunIds: string[], selectableStepRunIds: string[]) => void;
    onApplyingSelectionChange: (isApplying: boolean) => void;
}

const ConfirmationStepStatusSection: React.FC<ConfirmationStepStatusSectionProps> = ({
    status,
    title,
    color,
    orgId,
    appId,
    warnAboutNewerCommits,
    workflowId,
    workflowRunId,
    visible,
    releaseVersion,
    transactionType,
    totalCount,
    allStepRunIds,
    scrollRootRef,
    isDisabled = false,
    selectedStepRunIds,
    onRegisterFetchAll,
    onUnregisterFetchAll,
    onStepSelectionChange,
    onSectionSelectionChange,
    onApplyingSelectionChange,
}) => {
    const [isApplyingSelection, setIsApplyingSelection] = React.useState(false);
    const { stepRuns, isLoading, isFetching, hasMore, fetchMore, fetchAll } = useStepRunsSummary({
        orgId,
        workflowId,
        workflowRunId,
        stepRunIds: allStepRunIds,
        skip: !visible || allStepRunIds.length === 0,
    });
    const sentinelRef = useInfiniteScroll(hasMore, isFetching, fetchMore, scrollRootRef);

    React.useEffect(() => {
        onRegisterFetchAll(status, fetchAll);
        return () => onUnregisterFetchAll(status);
    }, [status, fetchAll, onRegisterFetchAll, onUnregisterFetchAll]);

    const selectableSteps = stepRuns.filter(
        (step) => isStepRunSelectableForTransactionOrError(transactionType, step) === null,
    );

    // Auto-select newly loaded selectable step runs if all previously loaded ones were selected.
    const selectedStepRunIdsRef = React.useRef(selectedStepRunIds);
    selectedStepRunIdsRef.current = selectedStepRunIds;
    const prevSelectableIdsRef = React.useRef<Set<string>>(new Set());
    React.useEffect(() => {
        const prevIds = prevSelectableIdsRef.current;
        const newSelectableSteps = selectableSteps.filter((s) => !prevIds.has(s.id));
        prevSelectableIdsRef.current = new Set(selectableSteps.map((s) => s.id));

        if (newSelectableSteps.length === 0) return;

        const currentSelection = selectedStepRunIdsRef.current;
        const prevAllSelected = prevIds.size > 0 && Array.from(prevIds).every((id) => currentSelection.has(id));
        if (prevAllSelected) {
            const newIds = newSelectableSteps.map((s) => s.id);
            onSectionSelectionChange(true, newIds, newIds);
        }
    }, [selectableSteps]);

    const allSelected = selectableSteps.length > 0 && selectableSteps.every((step) => selectedStepRunIds.has(step.id));
    const someSelected = selectableSteps.some((step) => selectedStepRunIds.has(step.id));
    const nonSelectableCount = stepRuns.length - selectableSteps.length;
    const allNonSelectable = stepRuns.length > 0 && selectableSteps.length === 0 && !hasMore;
    const showNonSelectable = allNonSelectable || (selectedStepRunIds.size > 0 && nonSelectableCount > 0);
    const selectedCount = allStepRunIds.filter((id) => selectedStepRunIds.has(id)).length;

    const handleSectionChange = async (checked: boolean) => {
        if (!checked) {
            onSectionSelectionChange(false, allStepRunIds, []);
            return;
        }

        // Optimistically select already-loaded selectable step runs while the full fetch is in progress.
        onSectionSelectionChange(
            true,
            allStepRunIds,
            selectableSteps.map((s) => s.id),
        );

        setIsApplyingSelection(true);
        onApplyingSelectionChange(true);
        try {
            const statusRuns = await fetchAll();
            const selectableStepRunIds = statusRuns
                .filter((step) => isStepRunSelectableForTransactionOrError(transactionType, step) === null)
                .map((step) => step.id);
            onSectionSelectionChange(true, allStepRunIds, selectableStepRunIds);
        } finally {
            setIsApplyingSelection(false);
            onApplyingSelectionChange(false);
        }
    };

    return (
        <Collapse
            ghost
            defaultActiveKey={['1']}
            expandIcon={({ isActive }) => (
                <CaretRightOutlined style={{ color: antGrey.grey6, fontSize: 12 }} rotate={isActive ? 90 : 0} />
            )}
            items={[
                {
                    key: '1',
                    label: (
                        <Flex align="center" justify="space-between" style={{ width: '100%', paddingRight: 8 }}>
                            <Flex align="center" gap={8}>
                                <Checkbox
                                    checked={allSelected || isApplyingSelection}
                                    indeterminate={someSelected && !allSelected && !isApplyingSelection}
                                    disabled={
                                        allStepRunIds.length === 0 ||
                                        isApplyingSelection ||
                                        isDisabled ||
                                        allNonSelectable
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                        void handleSectionChange(e.target.checked);
                                    }}
                                />
                                <Text strong style={{ fontSize: 14, color: color }}>
                                    {title}
                                </Text>
                                <Divider type="vertical" style={{ backgroundColor: antGrey.grey5, margin: 0 }} />
                                <Text style={{ fontSize: 12, color: antGrey.grey6 }}>
                                    {selectedCount} of {totalCount} selected
                                </Text>
                                {showNonSelectable && (
                                    <>
                                        <Text style={{ fontSize: 12, color: antGrey.grey5 }}>|</Text>
                                        <Text style={{ fontSize: 12, color: antGrey.grey6 }}>
                                            {nonSelectableCount} cannot be selected
                                        </Text>
                                    </>
                                )}
                            </Flex>
                            {isApplyingSelection && <Spin size="small" indicator={SmallSpinnerIndicator} />}
                        </Flex>
                    ),
                    children:
                        stepRuns.length > 0 || isLoading || hasMore ?
                            <>
                                {stepRuns.map((step) => {
                                    const errorMessage = isStepRunSelectableForTransactionOrError(
                                        transactionType,
                                        step,
                                    );
                                    const isSelectable = errorMessage === null;
                                    return (
                                        <Flex key={step.id} align="flex-start" gap={12} style={{ marginBottom: 12 }}>
                                            <Checkbox
                                                checked={selectedStepRunIds.has(step.id)}
                                                disabled={!isSelectable}
                                                onChange={(e) => onStepSelectionChange(step.id, e.target.checked)}
                                                style={{ marginTop: 12 }}
                                            />
                                            <div style={{ flex: 1 }}>
                                                <ConfirmationStepRunPanel
                                                    stepRun={step}
                                                    orgId={orgId}
                                                    appId={appId}
                                                    releaseVersion={releaseVersion}
                                                    warnAboutNewerCommits={warnAboutNewerCommits}
                                                    isSelected={selectedStepRunIds.has(step.id)}
                                                    isDisabled={!isSelectable}
                                                    onSelect={() => {
                                                        if (isSelectable) {
                                                            onStepSelectionChange(
                                                                step.id,
                                                                !selectedStepRunIds.has(step.id),
                                                            );
                                                        }
                                                    }}
                                                    errorMessage={errorMessage}
                                                />
                                            </div>
                                        </Flex>
                                    );
                                })}
                                {(isLoading || hasMore) && (
                                    <div
                                        ref={sentinelRef}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            padding: '8px 0',
                                            minHeight: 1,
                                        }}
                                    >
                                        {(isLoading || isFetching) && (
                                            <Spin size="small" indicator={SmallSpinnerIndicator} />
                                        )}
                                    </div>
                                )}
                            </>
                        :   null,
                },
            ]}
        />
    );
};

interface BatchTransactionModalProps {
    visible: boolean;
    onClose: () => void;
    transactionType: TransactionType;
    stepRunStatusData: StepRunStatusData[];
    appId: string;
    orgId: string;
    releaseId: string;
}

const BatchTransactionModal: React.FC<BatchTransactionModalProps> = ({
    visible,
    onClose,
    transactionType,
    stepRunStatusData,
    appId,
    orgId,
    releaseId,
}) => {
    const [form] = Form.useForm<BatchTransactionFormValues>();
    const [isFormValid, setIsFormValid] = React.useState(false);
    const [isSelectingAll, setIsSelectingAll] = React.useState(false);
    const [applyingSelectionCount, setApplyingSelectionCount] = React.useState(0);
    const isAnyApplyingSelection = applyingSelectionCount > 0;
    const fetchAllByStatusRef = React.useRef<Partial<Record<StepRunSummaryStatus, () => Promise<StepRun[]>>>>({});

    const workflowId = useSelector((state: RootState) => selectWorkflowId(state, appId));
    const workflowRunId = useSelector((state: RootState) => selectWorkflowRunId(state, releaseId));

    const shouldWarnAboutNewerCommits = useSelector((state: RootState) =>
        selectShouldWarnAboutNewerCommits(state, releaseId),
    );
    const warnAboutNewerCommits = transactionType === TransactionType.ReleaseVersion && shouldWarnAboutNewerCommits;

    const allStepRunIds = React.useMemo(
        () => stepRunStatusData.map((sd) => sd.stepRunId).filter((id): id is string => id !== null),
        [stepRunStatusData],
    );

    const stepRunsByStatus = React.useMemo(() => buildStepRunsByStatus(stepRunStatusData), [stepRunStatusData]);

    const allStepRuns = useSelector((state: RootState) =>
        selectStepRunsFromSummary(state, workflowRunId, allStepRunIds),
    );

    const stepRunsScrollContainerRef = React.useRef<HTMLDivElement>(null);

    const selectedStepRunIds = Form.useWatch('selectedStepRunIds', form) || [];
    const selectedStepRunIdSet = new Set(selectedStepRunIds);

    const handleFormValuesChange = React.useCallback(() => {
        // Defer validation to next tick so form store has committed; validateFields() inside
        // onValuesChange can otherwise run against stale state and fail (e.g. outOfDate).
        setTimeout(() => {
            const selectedIds = form.getFieldValue('selectedStepRunIds') || [];
            const hasSelectedSteps = selectedIds.length > 0;
            form.validateFields({ validateOnly: true })
                .then(() => setIsFormValid(hasSelectedSteps))
                .catch(() => setIsFormValid(false));
        }, 0);
    }, [form]);

    const setSelectedStepRunIds = React.useCallback(
        (ids: string[]) => {
            form.setFieldsValue({ selectedStepRunIds: ids } as BatchTransactionFormValues);
            handleFormValuesChange();
        },
        [form, handleFormValuesChange],
    );

    const selectableStepRuns = React.useMemo(
        () => allStepRuns.filter((sr) => isStepRunSelectableForTransactionOrError(transactionType, sr) === null),
        [allStepRuns, transactionType],
    );
    const selectableStepRunIds = React.useMemo(() => selectableStepRuns.map((sr) => sr.id), [selectableStepRuns]);
    const allSelectedSelected =
        selectableStepRunIds.length > 0 && selectableStepRunIds.every((id) => selectedStepRunIdSet.has(id));
    const someSelectedSelected = selectableStepRunIds.some((id) => selectedStepRunIdSet.has(id));
    const nonSelectableCount = allStepRunIds.length - selectableStepRunIds.length;
    const showNonSelectable = selectedStepRunIds.length > 0 && nonSelectableCount > 0;
    const isSelectAllChecked = allSelectedSelected || isSelectingAll;
    const isSelectAllIndeterminate = someSelectedSelected && !allSelectedSelected;

    const isViewingAppsInDemoMode = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));

    const handleStepSelectionChange = (stepRunId: string, checked: boolean) => {
        const currentIds = form.getFieldValue('selectedStepRunIds') || [];
        const newIds = checked ? [...currentIds, stepRunId] : currentIds.filter((id: string) => id !== stepRunId);
        setSelectedStepRunIds(newIds);
    };

    const handleSectionSelectionChange = (checked: boolean, stepRunIds: string[], selectableStepRunIds: string[]) => {
        const currentIds = (form.getFieldValue('selectedStepRunIds') || []) as string[];
        const currentSet = new Set(currentIds);

        if (!checked) {
            for (const id of stepRunIds) {
                currentSet.delete(id);
            }
            setSelectedStepRunIds(Array.from(currentSet));
            return;
        }

        // Replace section selection with only selectable ids from this section.
        for (const id of stepRunIds) {
            currentSet.delete(id);
        }
        for (const id of selectableStepRunIds) {
            currentSet.add(id);
        }
        setSelectedStepRunIds(Array.from(currentSet));
    };

    const handleRegisterFetchAll = React.useCallback(
        (status: StepRunSummaryStatus, fetchAll: () => Promise<StepRun[]>) => {
            fetchAllByStatusRef.current[status] = fetchAll;
        },
        [],
    );

    const handleUnregisterFetchAll = React.useCallback((status: StepRunSummaryStatus) => {
        delete fetchAllByStatusRef.current[status];
    }, []);

    const handleApplyingSelectionChange = React.useCallback((isApplying: boolean) => {
        setApplyingSelectionCount((prev) => (isApplying ? prev + 1 : Math.max(0, prev - 1)));
    }, []);

    const handleSelectAll = async (checked: boolean) => {
        if (!checked) {
            setIsSelectingAll(false);
            setSelectedStepRunIds([]);
            return;
        }

        // Optimistically select all known IDs immediately, then reconcile after remaining pages load.
        setIsSelectingAll(true);
        setSelectedStepRunIds(allStepRunIds);

        try {
            const allRunsByStatus = await Promise.all(
                stepRunSummaryStatuses.map(async (status) => {
                    const fetchAllForStatus = fetchAllByStatusRef.current[status];
                    if (!fetchAllForStatus || stepRunsByStatus[status].stepRunIds.length === 0) {
                        return [];
                    }
                    return fetchAllForStatus();
                }),
            );
            const allRuns = flatten(allRunsByStatus);
            const newIds = allRuns
                .filter((sr) => isStepRunSelectableForTransactionOrError(transactionType, sr) === null)
                .map((sr) => sr.id);
            setSelectedStepRunIds(newIds);
        } finally {
            setIsSelectingAll(false);
        }
    };

    const releaseVersion = useSelector((state: RootState) => selectReleaseVersion(state, releaseId));

    const [triggerBatchAction, isTriggerBatchActionLoading, triggerBatchActionResponseType, triggerBatchActionError] =
        useCallUpdatable(
            WorkflowRunUpdatable.TriggerBatchAction,
            {
                orgId,
                workflowId,
                workflowRunId,
            },
            allStepRunIds.join(','),
        );

    React.useEffect(() => {
        if (triggerBatchActionResponseType === RunwayResponseType.Success) {
            onClose();
            globalMessage.success('Batch action triggered!');
        }
    }, [triggerBatchActionResponseType]);

    React.useEffect(() => {
        if (triggerBatchActionError) {
            globalMessage.error(triggerBatchActionError);
        }
    }, [triggerBatchActionError]);

    const handleConfirm = async () => {
        try {
            const { selectedStepRunIds, ...rest } = await form.validateFields();
            const stepArchetype = stepRunStatusData?.[0]?.type;
            triggerBatchAction({
                targetStepRunIds: selectedStepRunIds,
                transactionType,
                stepArchetype,
                customRequestData: rest,
            });
        } catch (error) {
            globalMessage.error(error.message);
        }
    };

    return (
        <Modal
            title={modalTitleForTransactionType(transactionType)}
            open={visible}
            onCancel={onClose}
            okButtonProps={{
                loading: isTriggerBatchActionLoading,
                disabled: !isFormValid || isViewingAppsInDemoMode || isSelectingAll || isAnyApplyingSelection,
                icon: iconForTransactionType(transactionType, { useDefaultColor: true }),
            }}
            okText={
                isAnyApplyingSelection ? 'Selecting steps...' : (
                    confirmButtonTextForTransactionType(transactionType, selectedStepRunIds.length)
                )
            }
            onOk={handleConfirm}
            cancelText="Cancel"
            destroyOnClose
            width={800}
        >
            <Text style={{ color: antGrey.grey7, display: 'block', marginBottom: 24 }}>
                {modalDescriptionForTransactionType(transactionType)}
            </Text>

            <Form form={form} layout="vertical" onValuesChange={handleFormValuesChange}>
                <Form.Item
                    name="selectedStepRunIds"
                    initialValue={[]}
                    preserve={false}
                    rules={[
                        {
                            validator: (_, value) => {
                                if (!value || value.length === 0) {
                                    return Promise.reject(new Error('Please select at least one step'));
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                    hidden
                >
                    <input type="hidden" />
                </Form.Item>
                {extraOptionsForTransactionType(transactionType, allStepRuns)}
            </Form>

            <Flex align="center" justify="space-between" style={{ marginBottom: 16 }}>
                <Flex align="center" gap={12}>
                    <Checkbox
                        checked={isSelectAllChecked}
                        indeterminate={isSelectAllIndeterminate}
                        disabled={allStepRunIds.length === 0 || isSelectingAll}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                    <Text strong>Select all</Text>
                    <Text style={{ fontSize: 12, color: antGrey.grey6 }}>
                        {selectedStepRunIds.length} of {allStepRunIds.length} selected
                    </Text>
                    {showNonSelectable && (
                        <>
                            <Text style={{ fontSize: 12, color: antGrey.grey5 }}>|</Text>
                            <Text style={{ fontSize: 12, color: antGrey.grey6 }}>
                                {nonSelectableCount} cannot be selected
                            </Text>
                        </>
                    )}
                </Flex>
                {isSelectingAll && <Spin size="small" indicator={SmallSpinnerIndicator} />}
            </Flex>
            <Divider style={{ margin: '16px 0' }} />

            <div ref={stepRunsScrollContainerRef} style={{ maxHeight: 500, overflowY: 'auto' }}>
                {stepRunsByStatus.pending.totalCount > 0 && (
                    <ConfirmationStepStatusSection
                        status="pending"
                        title="Waiting"
                        totalCount={stepRunsByStatus.pending.totalCount}
                        allStepRunIds={stepRunsByStatus.pending.stepRunIds}
                        workflowId={workflowId}
                        workflowRunId={workflowRunId}
                        visible={visible}
                        scrollRootRef={stepRunsScrollContainerRef}
                        isDisabled={isSelectingAll}
                        color={runwayColors.statusYellow}
                        orgId={orgId}
                        appId={appId}
                        warnAboutNewerCommits={warnAboutNewerCommits}
                        releaseVersion={releaseVersion}
                        transactionType={transactionType}
                        selectedStepRunIds={selectedStepRunIdSet}
                        onRegisterFetchAll={handleRegisterFetchAll}
                        onUnregisterFetchAll={handleUnregisterFetchAll}
                        onStepSelectionChange={handleStepSelectionChange}
                        onSectionSelectionChange={handleSectionSelectionChange}
                        onApplyingSelectionChange={handleApplyingSelectionChange}
                    />
                )}
                {stepRunsByStatus.ready.totalCount > 0 && (
                    <ConfirmationStepStatusSection
                        status="ready"
                        title="Ready"
                        totalCount={stepRunsByStatus.ready.totalCount}
                        allStepRunIds={stepRunsByStatus.ready.stepRunIds}
                        workflowId={workflowId}
                        workflowRunId={workflowRunId}
                        visible={visible}
                        scrollRootRef={stepRunsScrollContainerRef}
                        isDisabled={isSelectingAll}
                        color={runwayColors.statusGreen}
                        orgId={orgId}
                        appId={appId}
                        warnAboutNewerCommits={warnAboutNewerCommits}
                        releaseVersion={releaseVersion}
                        transactionType={transactionType}
                        selectedStepRunIds={selectedStepRunIdSet}
                        onRegisterFetchAll={handleRegisterFetchAll}
                        onUnregisterFetchAll={handleUnregisterFetchAll}
                        onStepSelectionChange={handleStepSelectionChange}
                        onSectionSelectionChange={handleSectionSelectionChange}
                        onApplyingSelectionChange={handleApplyingSelectionChange}
                    />
                )}
                {stepRunsByStatus.blocked.totalCount > 0 && (
                    <ConfirmationStepStatusSection
                        status="blocked"
                        title="Blocked"
                        totalCount={stepRunsByStatus.blocked.totalCount}
                        allStepRunIds={stepRunsByStatus.blocked.stepRunIds}
                        workflowId={workflowId}
                        workflowRunId={workflowRunId}
                        visible={visible}
                        scrollRootRef={stepRunsScrollContainerRef}
                        isDisabled={isSelectingAll}
                        color={runwayColors.statusRed}
                        orgId={orgId}
                        appId={appId}
                        warnAboutNewerCommits={warnAboutNewerCommits}
                        releaseVersion={releaseVersion}
                        transactionType={transactionType}
                        selectedStepRunIds={selectedStepRunIdSet}
                        onRegisterFetchAll={handleRegisterFetchAll}
                        onUnregisterFetchAll={handleUnregisterFetchAll}
                        onStepSelectionChange={handleStepSelectionChange}
                        onSectionSelectionChange={handleSectionSelectionChange}
                        onApplyingSelectionChange={handleApplyingSelectionChange}
                    />
                )}
                {stepRunsByStatus.inactive.totalCount > 0 && (
                    <ConfirmationStepStatusSection
                        status="inactive"
                        title="Inactive"
                        totalCount={stepRunsByStatus.inactive.totalCount}
                        allStepRunIds={stepRunsByStatus.inactive.stepRunIds}
                        workflowId={workflowId}
                        workflowRunId={workflowRunId}
                        visible={visible}
                        scrollRootRef={stepRunsScrollContainerRef}
                        isDisabled={isSelectingAll}
                        color={antGrey.grey6}
                        orgId={orgId}
                        appId={appId}
                        warnAboutNewerCommits={warnAboutNewerCommits}
                        releaseVersion={releaseVersion}
                        transactionType={transactionType}
                        selectedStepRunIds={selectedStepRunIdSet}
                        onRegisterFetchAll={handleRegisterFetchAll}
                        onUnregisterFetchAll={handleUnregisterFetchAll}
                        onStepSelectionChange={handleStepSelectionChange}
                        onSectionSelectionChange={handleSectionSelectionChange}
                        onApplyingSelectionChange={handleApplyingSelectionChange}
                    />
                )}
                {stepRunsByStatus.skipped.totalCount > 0 && (
                    <ConfirmationStepStatusSection
                        status="skipped"
                        title="Skipped"
                        totalCount={stepRunsByStatus.skipped.totalCount}
                        allStepRunIds={stepRunsByStatus.skipped.stepRunIds}
                        workflowId={workflowId}
                        workflowRunId={workflowRunId}
                        visible={visible}
                        scrollRootRef={stepRunsScrollContainerRef}
                        isDisabled={isSelectingAll}
                        color={antGrey.grey6}
                        orgId={orgId}
                        appId={appId}
                        warnAboutNewerCommits={warnAboutNewerCommits}
                        releaseVersion={releaseVersion}
                        transactionType={transactionType}
                        selectedStepRunIds={selectedStepRunIdSet}
                        onRegisterFetchAll={handleRegisterFetchAll}
                        onUnregisterFetchAll={handleUnregisterFetchAll}
                        onStepSelectionChange={handleStepSelectionChange}
                        onSectionSelectionChange={handleSectionSelectionChange}
                        onApplyingSelectionChange={handleApplyingSelectionChange}
                    />
                )}
            </div>
        </Modal>
    );
};

export const BatchTransactionBar: React.FC<BatchTransactionBarProps> = ({
    orgId,
    appId,
    releaseId,
    transactionTypes,
    stepRunStatusData,
}) => {
    const [activeModal, setActiveModal] = React.useState<TransactionType | null>(null);

    if (transactionTypes.length === 0) {
        return null;
    }

    // Group transaction types: ungrouped ones and grouped ones by their group type
    const ungroupedTypes: TransactionType[] = [];
    const groupedTypesMap = new Map<TransactionGroupType, TransactionType[]>();

    transactionTypes.forEach((transactionType) => {
        const group = getTransactionGroupForType(transactionType);
        if (group === null) {
            ungroupedTypes.push(transactionType);
        } else {
            const existing = groupedTypesMap.get(group) || [];
            existing.push(transactionType);
            groupedTypesMap.set(group, existing);
        }
    });

    const renderTransactionButton = (transactionType: TransactionType) => (
        <Button
            key={transactionType}
            icon={iconForTransactionType(transactionType)}
            onClick={() => setActiveModal(transactionType)}
            {...buttonPropsForTransactionType(transactionType)}
        >
            {actionForTransactionType(transactionType)}
        </Button>
    );

    const renderTransactionGroupDropdown = (groupType: TransactionGroupType, types: TransactionType[]) => {
        const menuItems: MenuProps['items'] = types.map((transactionType) => ({
            key: transactionType,
            icon: iconForTransactionType(transactionType),
            label: actionForTransactionType(transactionType),
            onClick: () => setActiveModal(transactionType),
        }));

        return (
            <Dropdown key={groupType} menu={{ items: menuItems }}>
                <Button
                    icon={iconForTransactionGroupType(groupType)}
                    {...buttonPropsForTransactionGroupType(groupType)}
                >
                    {actionForTransactionGroupType(groupType)}
                    <DownOutlined />
                </Button>
            </Dropdown>
        );
    };

    return (
        <>
            <Footer style={styles.footerStyle}>
                <Space>
                    {ungroupedTypes.map(renderTransactionButton)}
                    {Array.from(groupedTypesMap.entries()).map(([groupType, types]) =>
                        renderTransactionGroupDropdown(groupType, types),
                    )}
                </Space>
            </Footer>

            <BatchTransactionModal
                orgId={orgId}
                appId={appId}
                releaseId={releaseId}
                visible={activeModal !== null}
                onClose={() => setActiveModal(null)}
                transactionType={activeModal}
                stepRunStatusData={stepRunStatusData}
            />
        </>
    );
};
